import type {
  ApiKeyResponse,
  RotateApiKeyResponse,
} from '@monorepo/shared/schemas/api-keys'
import {
  AlertTriangle,
  Check,
  Copy,
  Eye,
  EyeOff,
  RefreshCw,
} from 'lucide-react'
import { useState } from 'react'
import { Button } from '@/shared/components/ui/button'
import { Card, CardContent } from '@/shared/components/ui/card'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/shared/components/ui/dialog'
import { Input } from '@/shared/components/ui/input'
import { extractErrorMessage } from '@/shared/lib/error-utils'
import { toast } from '@/shared/lib/toast'
import { apiKeysService } from '../services/apiKeysService'

interface ApiKeyCardProps {
  apiKey: ApiKeyResponse
  onKeyRotated: (response: RotateApiKeyResponse) => void
}

export function ApiKeyCard({ apiKey, onKeyRotated }: ApiKeyCardProps) {
  const [isRevealed, setIsRevealed] = useState(false)
  const [isRegenerating, setIsRegenerating] = useState(false)
  const [showConfirmDialog, setShowConfirmDialog] = useState(false)
  const [copied, setCopied] = useState(false)

  const hasFullKey = !!apiKey.key

  const handleCopy = async () => {
    if (!apiKey.key) return
    try {
      await navigator.clipboard.writeText(apiKey.key)
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
      toast.success('API key copied to clipboard')
    } catch (error) {
      toast.error(extractErrorMessage(error))
    }
  }

  const handleRegenerate = async () => {
    setIsRegenerating(true)
    try {
      const response = await apiKeysService.rotateApiKey(apiKey.id)
      onKeyRotated(response)
      setShowConfirmDialog(false)
    } catch (error) {
      toast.error(extractErrorMessage(error))
    } finally {
      setIsRegenerating(false)
    }
  }

  const formatKey = () => {
    if (!apiKey.key) {
      return `${apiKey.key_prefix}${'•'.repeat(28)}`
    }
    if (isRevealed) {
      return apiKey.key
    }
    const start = apiKey.key.substring(0, 8)
    const end = apiKey.key.substring(apiKey.key.length - 4)
    const masked = '•'.repeat(apiKey.key.length - 12)
    return `${start}${masked}${end}`
  }

  return (
    <Card className='bg-transparent'>
      <CardContent className='space-y-6 pt-6'>
        <h3 className='text-xl font-semibold'>API Key</h3>

        {/* One-time warning banner when full key is available */}
        {hasFullKey && (
          <div className='bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4'>
            <p className='text-sm text-amber-800 dark:text-amber-200'>
              <strong>Copy your API key now.</strong> For security, the full key
              won't be shown again after you leave this page.
            </p>
          </div>
        )}

        {/* API Key Display */}
        <div className='flex items-center gap-2'>
          <Input
            value={formatKey()}
            readOnly
            className='font-mono text-sm'
            style={{ fontFamily: 'monospace' }}
          />
          {hasFullKey && (
            <Button
              variant='outline'
              size='sm'
              onClick={() => setIsRevealed(!isRevealed)}
              className='px-3'
            >
              {isRevealed ? (
                <EyeOff className='w-4 h-4' />
              ) : (
                <Eye className='w-4 h-4' />
              )}
            </Button>
          )}
          <Button
            variant='outline'
            size='sm'
            onClick={handleCopy}
            disabled={!hasFullKey}
            className='px-3'
          >
            {copied ? (
              <Check className='w-4 h-4 text-green-600' />
            ) : (
              <Copy className='w-4 h-4' />
            )}
          </Button>
        </div>

        {!hasFullKey && (
          <p className='text-xs text-muted-foreground'>
            The full key is only shown once at creation. Regenerate to get a new
            key.
          </p>
        )}

        {/* Actions */}
        <div className='flex justify-end pt-4'>
          <Dialog open={showConfirmDialog} onOpenChange={setShowConfirmDialog}>
            <DialogTrigger asChild>
              <Button variant='outline' className='gap-2'>
                <RefreshCw className='w-4 h-4' />
                Regenerate Key
              </Button>
            </DialogTrigger>
            <DialogContent>
              <DialogHeader>
                <DialogTitle className='flex items-center gap-2'>
                  <AlertTriangle className='w-5 h-5 text-amber-500' />
                  Regenerate API Key
                </DialogTitle>
                <DialogDescription>
                  This will permanently invalidate your current API key and
                  generate a new one. Any applications using the current key
                  will need to be updated with the new key.
                </DialogDescription>
              </DialogHeader>
              <div className='space-y-4'>
                <div className='bg-amber-50 dark:bg-amber-950/20 border border-amber-200 dark:border-amber-800 rounded-lg p-4'>
                  <p className='text-sm text-amber-800 dark:text-amber-200'>
                    <strong>Warning:</strong> This action cannot be undone. Make
                    sure to update all applications and integrations with the
                    new key before proceeding.
                  </p>
                </div>

                <div className='flex justify-end gap-2'>
                  <Button
                    variant='outline'
                    onClick={() => setShowConfirmDialog(false)}
                    disabled={isRegenerating}
                  >
                    Cancel
                  </Button>
                  <Button
                    variant='destructive'
                    onClick={handleRegenerate}
                    disabled={isRegenerating}
                    className='gap-2'
                  >
                    {isRegenerating ? (
                      <>
                        <RefreshCw className='w-4 h-4 animate-spin' />
                        Regenerating...
                      </>
                    ) : (
                      <>
                        <RefreshCw className='w-4 h-4' />
                        Regenerate Key
                      </>
                    )}
                  </Button>
                </div>
              </div>
            </DialogContent>
          </Dialog>
        </div>
      </CardContent>
    </Card>
  )
}
